var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { Auth, Hub } from 'aws-amplify';
import { METRIC_EVENT_NAMES, trackCustomEvent } from '@amzn/austin-core';
// Enum used for collecting session event metrics (i.e. events sent to CloudWatch).
export var SESSION_EVENT_TYPE;
(function (SESSION_EVENT_TYPE) {
    SESSION_EVENT_TYPE["CONFIRM_EXPIRED"] = "SessionExpired-Confirmed";
    SESSION_EVENT_TYPE["CONFIRM_EXPIRING"] = "SessionWillExpire-Confirmed";
    SESSION_EVENT_TYPE["EXPIRED"] = "SessionExpired";
    SESSION_EVENT_TYPE["EXPIRING"] = "SessionWillExpire";
    SESSION_EVENT_TYPE["SESSION_START_FAILED"] = "SessionStartFailed";
})(SESSION_EVENT_TYPE || (SESSION_EVENT_TYPE = {}));
// Enum used for dispatching/listening to custom Hub events.
export var SESSION_TOPIC;
(function (SESSION_TOPIC) {
    SESSION_TOPIC["DISMISS_NOTIFICATION"] = "session_dismiss_notification";
    SESSION_TOPIC["EXPIRED"] = "session_expired";
    SESSION_TOPIC["WILL_EXPIRE"] = "session_will_expire";
})(SESSION_TOPIC || (SESSION_TOPIC = {}));
// The following key is used for setting retrieving the redirect URI from localStorage.
export var REDIRECT_URI_KEY = 'redirect_uri';
/**
 * Value maps to RefreshTokenValidity defined in the AustinEhsAuthNInfrastructure CloudFormation template.
 * Any changes to the template should be reflected here. Note that the value represents number of hours that
 * the refresh token is valid for. As of 01/10/2023 the auth template is setting an expiration of 12 hours.
 *
 * @see https://code.amazon.com/packages/AustinEhsAuthNInfrastructure/blobs/mainline/--/configuration/cloudFormation/auth.template.yml
 */
var ONE_DAY_IN_SECONDS = 86400;
var REFRESH_TOKEN_VALIDITY_IN_SECONDS = ONE_DAY_IN_SECONDS / 2;
export var SESSION_TIMER_INTERVAL = 5000;
export var SESSION_START_RETRY_ATTEMPTS = 3;
/**
 * The BroadcastChannel Web API is being used to communicate events across tabs/windows
 * so that we can suppress active modals in other tabs when the user interacts with a modal
 * in their active tab.
 *
 * @see https://sim.amazon.com/issues/AUSTIN-20367
 * @see https://developer.mozilla.org/en-US/docs/Web/API/BroadcastChannel
 */
export var SESSION_BROADCAST_CHANNEL = 'austin-ehs-session-broadcast-channel';
var broadcastChannel = new window.BroadcastChannel(SESSION_BROADCAST_CHANNEL);
/**
 * This will serve as our in-memory flag to suppress further notifications to the user that their session
 * expires soon. Users are forced to interact with the modal to confirm so we should only ever show it once.
 *
 * @todo
 * See about moving this to localStorage as a page refresh will also reset the internal flag. We'll need to
 * think through properly invalidating the flag as there are several edge cases to consider.
 */
var hasShownSessionWillExpireNotice = false;
/**
 * Will broadcast to all windows that user has acknowledged the sesion is expiring so that
 * dialogs in inactive tabs can close automatically, then logs the event in CloudWatch.
 */
export function broadcastAcknowledgeSessionExpiringEvent() {
    broadcastSessionFlagsResetEvent();
    recordSessionEvent(SESSION_EVENT_TYPE.CONFIRM_EXPIRING);
}
/**
 * Will broadcast to all windowss that the session will expire which will trigger
 * a dialog so that the user can confirm, then logs the event in CloudWatch.
 *
 * @todo
 * Dispatching this event using both Hub and BroadcastChannel is redundant. While this
 * is not desired, leavingn for now until we can find a way to properly mock and test
 * BroadcastChannel in unit tests.
 */
function broadcastSessionWillExpireEvent(minutesRemaining) {
    Hub.dispatch(SESSION_TOPIC.WILL_EXPIRE, {
        data: {
            minutesRemaining: minutesRemaining
        },
        event: SESSION_TOPIC.WILL_EXPIRE
    });
    broadcastChannel.postMessage(SESSION_TOPIC.WILL_EXPIRE);
    recordSessionEvent(SESSION_EVENT_TYPE.EXPIRING);
}
/**
 * Will broadcast to all windows that the session has expired which will trigger a
 * dialog so that the user can confirm and reauthenticate.
 *
 * @todo
 * Dispatching this event using both Hub and BroadcastChannel is redundant. While this
 * is not desired, leavingn for now until we can find a way to properly mock and test
 * BroadcastChannel in unit tests.
 */
function broadcastSessionExpiredEvent() {
    Hub.dispatch(SESSION_TOPIC.EXPIRED, {
        event: SESSION_TOPIC.EXPIRED
    });
    broadcastChannel.postMessage(SESSION_TOPIC.EXPIRED);
    recordSessionEvent(SESSION_EVENT_TYPE.EXPIRED);
}
/**
 * Will broadcast to all windows that all session flags have been reset so that
 * all active modals can be automatically closed in inactive windows.
 */
function broadcastSessionFlagsResetEvent() {
    broadcastChannel.postMessage(SESSION_TOPIC.DISMISS_NOTIFICATION);
}
/**
 * Will record events related to session expiration, both programmatic events
 * as well as user interactions with the session dialogs (expired/will expire).
 */
export function recordSessionEvent(type) {
    trackCustomEvent({
        attributes: {
            action: type,
            component: 'Session',
            module: 'AustinEhsPlatformFrontend'
        },
        immediate: true,
        name: METRIC_EVENT_NAMES.CUSTOM_EVENT
    });
}
/**
 * Redirects to the Cognito login page with the appropriate redirect URL. Note that the REDIRECT_URI has to
 * match one of the callback URL's defined in the Cognito console otherwise it will show an error page. For
 * that reason we store the current location in sessionStorage so that we can actually redirect to the correct
 * landing page after the Cognito redirect into our application.
 */
export function redirectToLoginPage(recordConfirmEvent) {
    if (recordConfirmEvent === void 0) { recordConfirmEvent = false; }
    if (recordConfirmEvent) {
        recordSessionEvent(SESSION_EVENT_TYPE.CONFIRM_EXPIRED);
    }
    sessionStorage.setItem(REDIRECT_URI_KEY, window.location.href);
    Auth.configure();
    Auth.federatedSignIn();
}
/**
 * Retrieves sessionStorage value and redirects user if the redirect URI is different than the current location.
 * This will be invoked when the session is initialized, and if returning from an expired session, will redirect
 * the user back to the page they were on when their session expired.
 */
export function redirectToOriginator() {
    var redirectUri = sessionStorage.getItem(REDIRECT_URI_KEY) || window.location.href;
    sessionStorage.removeItem(REDIRECT_URI_KEY);
    if (redirectUri !== window.location.href) {
        window.location.href = encodeURI(redirectUri);
    }
}
/**
 * Starts the session timer and dispatches events when session expiration is approaching, as well as when
 * session HAS expired. To determine when the session will expire we use the "auth_time" + 24 hours which
 * is when the refresh token will expire (1 day).
 */
export function startSessionTimer(retryAttempt) {
    if (retryAttempt === void 0) { retryAttempt = 0; }
    return __awaiter(this, void 0, void 0, function () {
        var minutesBeforeExpirationWarning, accessToken, expiresTimestamp_1, getMinutesRemaining_1, intervalId_1, error_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    minutesBeforeExpirationWarning = 15;
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, Auth.currentSession()];
                case 2:
                    accessToken = (_a.sent()).accessToken;
                    expiresTimestamp_1 = accessToken.payload.auth_time + REFRESH_TOKEN_VALIDITY_IN_SECONDS;
                    getMinutesRemaining_1 = function () {
                        var currentTimestamp = Math.trunc(new Date().getTime() / 1000);
                        var minutesRemaining = Math.ceil((expiresTimestamp_1 - currentTimestamp) / 60);
                        return minutesRemaining;
                    };
                    if (getMinutesRemaining_1() <= 0) {
                        broadcastSessionExpiredEvent();
                    }
                    else {
                        intervalId_1 = setInterval(function () {
                            var minutesRemaining = getMinutesRemaining_1();
                            if (minutesRemaining <= 0) {
                                broadcastSessionExpiredEvent();
                                clearInterval(intervalId_1);
                            }
                            else if (!hasShownSessionWillExpireNotice &&
                                minutesRemaining <= minutesBeforeExpirationWarning) {
                                hasShownSessionWillExpireNotice = true;
                                broadcastSessionWillExpireEvent(minutesRemaining);
                            }
                            return minutesRemaining;
                        }, SESSION_TIMER_INTERVAL);
                    }
                    return [3 /*break*/, 4];
                case 3:
                    error_1 = _a.sent();
                    /**
                     * @note
                     * The Auth class will return one of the following errors as strings:
                     *
                     * - No User Pool in the configuration
                     * - Failed to get the current session
                     * - Failed to get the current user
                     *
                     * As there can be intermittent failures, we're going to retry several times
                     * before forcing the user out.
                     */
                    if (retryAttempt === SESSION_START_RETRY_ATTEMPTS) {
                        recordSessionEvent(SESSION_EVENT_TYPE.SESSION_START_FAILED);
                        broadcastSessionExpiredEvent();
                    }
                    else {
                        setTimeout(function () { return startSessionTimer(retryAttempt + 1); }, SESSION_TIMER_INTERVAL);
                    }
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
            }
        });
    });
}
/**
 * Retrieves and returns the jwtToken for the currently authenticated user.
 */
export function getAccessToken() {
    return __awaiter(this, void 0, void 0, function () {
        var accessToken, ex_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    accessToken = '';
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, Auth.currentAuthenticatedUser().then(function (user) {
                            accessToken = user.signInUserSession.idToken.jwtToken;
                        })];
                case 2:
                    _a.sent();
                    return [3 /*break*/, 4];
                case 3:
                    ex_1 = _a.sent();
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/, accessToken];
            }
        });
    });
}
/**
 * Determines if access tokens exist (jwtToken) so that the session can be initiated.
 */
export function hasAccessTokens() {
    return __awaiter(this, void 0, void 0, function () {
        var e_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, Auth.currentAuthenticatedUser()];
                case 1:
                    _a.sent();
                    return [3 /*break*/, 3];
                case 2:
                    e_1 = _a.sent();
                    return [2 /*return*/, false];
                case 3: return [2 /*return*/, true];
            }
        });
    });
}
/**
 * Subscribes to auth messages to handle sign in and session expiration. Also starts the session timer.
 */
export function initializeSession(onUserProfileChange) {
    return __awaiter(this, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, Auth.currentAuthenticatedUser().then(onUserProfileChange)];
                case 1:
                    _a.sent();
                    startSessionTimer();
                    // Broadcast a message to ensure any active dialogs in other tabs are closed since the session
                    // is being reset. In the event of a manual refresh, session timer logic will broadcast the
                    // appropriate event to relaunch dialogs if the session is expiring or has expired.
                    broadcastSessionFlagsResetEvent();
                    return [2 /*return*/];
            }
        });
    });
}
/**
 * Broadcasts that session has expired so that other open tabs can display a modal,
 * then signs the user out and redirects back to the Cognito sign in page.
 */
export function signOut() {
    return __awaiter(this, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    broadcastSessionExpiredEvent();
                    return [4 /*yield*/, Auth.signOut()];
                case 1:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    });
}
